import * as React from "react";
import { Box, Button, Container, Dialog, DialogContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAcceptLegal } from "@/api/legal";
import { useLogout } from "@/api/me";
import { SelfRemovalDialog } from "@/features/account-link/SelfRemovalDialog";
import { TermsAcceptanceForm } from "@/features/legal/TermsAcceptanceForm";

/** The blocking terms screen for a signed-in user (Terms 2.4).
 *
 *  ``RequireAuth`` renders it instead of the app while ``/auth/me``
 *  reports ``legal_required``, or after a write came back 428. It cannot
 *  be dismissed (no ``onClose``, so Escape and the backdrop do nothing):
 *  the only ways on are Accept and Sign out, or deleting the account
 *  instead (``DELETE /auth/me`` is open to a user who has not accepted). */
export function TermsGate(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const accept = useAcceptLegal();
  const logout = useLogout();
  const [removing, setRemoving] = React.useState(false);

  return (
    <Dialog
      open
      fullScreen
      aria-labelledby="terms-gate-title"
      data-testid="terms-gate"
    >
      <DialogContent>
        <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
          <Box
            component="img"
            src="/brand/repoworks_logo_white.svg"
            alt=""
            aria-hidden
            sx={{ height: 32, width: "auto", mb: 3 }}
          />
          <TermsAcceptanceForm
            intro={
              <>
                <Typography variant="h5" component="h1" id="terms-gate-title">
                  {t("legal.gate.title")}
                </Typography>
                <Typography sx={{ mt: 2 }}>
                  {t("legal.gate.body")}
                </Typography>
              </>
            }
            secondaryLabel={t("legal.gate.sign_out")}
            onAccept={() =>
              accept.mutate({
                accept_terms: true,
                accept_privacy: true,
                ui_language: i18n.resolvedLanguage ?? i18n.language,
              })
            }
            onSecondary={() => logout.mutate()}
            busy={accept.isPending || logout.isPending}
            error={accept.isError ? t("legal.accept.failed") : null}
          />
          <Button
            variant="text"
            color="error"
            size="small"
            sx={{ mt: 3 }}
            onClick={() => setRemoving(true)}
          >
            {t("legal.gate.delete_instead")}
          </Button>
          <SelfRemovalDialog open={removing} onClose={() => setRemoving(false)} />
        </Container>
      </DialogContent>
    </Dialog>
  );
}
